// One candidate: /growth/finder/candidates/:id
//
// The facts, why it was suggested, how the priority adds up, Add to leads and
// Reject, and the separate preview-data step: team names, schedules, scores and
// standings as plain facts, stored as a draft for the Preview stage.

import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '@/api'
import { errorMessage, isApiError } from '@/api/client'
import type { Candidate, PreviewDraft } from '@/api/growthInsights'
import { PageHeader } from '@/components/shell/PageHeader'
import { Button, ButtonLink, Card, CardHeader, ConfirmDialog, EmailText, EmptyState, ErrorState, InlineAlert, KeyValueList, LoadingState, useToast } from '@/components/ui'
import { formatCount } from '@/lib/format'
import { LEAD_TYPE_LABELS, VERIFY_RUN_WORDS } from '@/lib/growth'
import { candidatePlace, SOURCE_LABELS } from '@/lib/growthInsights'
import { useDocumentTitle } from '@/lib/hooks'
import { invalidateQueries, useQuery } from '@/lib/query'
import { useActivityRun, useFinderStatus, usePoll } from '@/lib/useFinder'
import { ActivityDetails, ActivityRunBanner } from './activityShared'
import { CandidateStatusBadge } from './finderShared'
import { BucketBadge, SiteSignalList } from './platformShared'
import { CurrentPlatform, FailedReasons, VerifyDetails } from './verifyShared'
import { SafeExternalLink } from './shared'

function Priority({ candidate: c }: { candidate: Candidate }) {
  return (
    <Card>
      <CardHeader title={`Priority ${c.priority}`} description="Becomes the lead's priority when it's added." />
      {c.priority_reasons.length === 0 ? (
        <p className="text-[13px] text-muted">No points: nothing on the page earned any.</p>
      ) : (
        <ul className="grid gap-1.5 text-[13px]">
          {c.priority_reasons.map((r) => (
            <li key={r.key} className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 text-ink">{r.label}</span>
              <span className="shrink-0 font-mono text-xs text-muted tabular">{r.points > 0 ? `+${r.points}` : `−${Math.abs(r.points)}`}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

function Draft({ draft }: { draft: PreviewDraft }) {
  return (
    <div className="grid gap-3 text-[13px]">
      <p className="text-muted">
        {formatCount(draft.teams.length)} {draft.teams.length === 1 ? 'team' : 'teams'}, {formatCount(draft.games.length)} {draft.games.length === 1 ? 'game' : 'games'}.
        {draft.tables_skipped > 0 ? ` ${formatCount(draft.tables_skipped)} ${draft.tables_skipped === 1 ? 'table' : 'tables'} about people left alone.` : ''}
      </p>
      {draft.teams.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5">
          {draft.teams.slice(0, 24).map((t) => (
            <li key={t.name} className="rounded-md bg-hover px-2 py-0.5 text-xs text-ink">{t.name}</li>
          ))}
        </ul>
      ) : null}
      {draft.games.length > 0 ? (
        <ul className="grid gap-1 text-xs text-muted">
          {draft.games.slice(0, 5).map((g, i) => (
            <li key={i} className="break-words">
              {[g.date, g.time].filter(Boolean).join(', ')}{g.date || g.time ? ': ' : ''}{g.home} v {g.away}
              {g.home_score !== null && g.away_score !== null ? ` (${g.home_score}–${g.away_score})` : ''}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

function PreviewData({ candidate: c, draft, onChange }: { candidate: Candidate; draft: PreviewDraft | null; onChange: () => void }) {
  const toast = useToast()
  const { data: status } = useFinderStatus()
  const [runId, setRunId] = useState<number | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  const { data: run, refetch } = useQuery(runId ? ['growth', 'run', runId] : null, (signal) => api.growth.finder.run(runId as number, signal))
  const running = run?.run.status === 'running'
  usePoll(running, refetch)
  const finished = !!run && run.run.id === runId && !running
  // The draft arrives when the run ends, so the page reads it again then.
  useEffect(() => {
    if (finished) onChange()
  }, [finished, onChange])
  const finishedNote = finished ? run.run.note : null

  const extract = async () => {
    setProblem(null)
    try {
      const started = await api.growth.finder.startPreviewDraft(c.id)
      setRunId(started.id)
      invalidateQueries('growth')
      onChange()
    } catch (err) {
      setProblem(errorMessage(err))
    }
  }

  const handOff = async () => {
    setProblem(null)
    try {
      await api.growth.finder.handOffPreview(c.id)
      invalidateQueries('growth')
      toast.success('Handed to Preview sites.')
      onChange()
    } catch (err) {
      setProblem(errorMessage(err))
    }
  }

  return (
    <Card>
      <CardHeader title="Preview data" description="Team names, schedules, scores and standings from this public page, as plain facts. Never player data." />
      <div className="grid gap-3">
        {problem ? <InlineAlert tone="bad">{problem}</InlineAlert> : null}
        {finishedNote ? <InlineAlert tone="warn">{finishedNote}</InlineAlert> : null}
        {c.stored_only ? <p className="text-[13px] text-muted">This link is stored, not fetched, so there's nothing to read.</p> : null}
        {draft ? <Draft draft={draft} /> : null}
        {draft?.handed_off_at ? <p className="text-[13px] text-good">Handed to Preview sites.</p> : null}
        {draft?.handoff?.serving === false ? (
          <InlineAlert tone="warn">This preview isn't live, so a campaign using {'{{preview_url}}'} will skip this lead. Check it under Previews.</InlineAlert>
        ) : null}
        {!c.stored_only ? (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" loading={running} onClick={() => void extract()}>
              {draft ? 'Extract again' : 'Extract preview data'}
            </Button>
            {draft ? (
              <Button size="sm" variant="primary" disabled={!c.lead_id || !status?.preview_handoff || !!draft.handed_off_at} onClick={() => void handOff()}>
                Hand to Preview sites
              </Button>
            ) : null}
          </div>
        ) : null}
        {draft && !draft.handed_off_at ? (
          !c.lead_id ? <p className="text-xs text-muted">A preview belongs to a lead: add this candidate first.</p>
          : status && !status.preview_handoff ? <p className="text-xs text-muted">The Preview stage isn't connected yet. The draft is saved.</p>
          : null
        ) : null}
      </div>
    </Card>
  )
}

export default function CandidateDetailPage() {
  const { id: rawId } = useParams()
  const id = Number(rawId)
  const validId = Number.isInteger(id) && id > 0
  const navigate = useNavigate()
  const toast = useToast()
  const [confirm, setConfirm] = useState<'add' | 'reject' | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const verifyRun = useActivityRun('verify')

  const { data, error, status, refetch } = useQuery(validId ? ['growth', 'candidate', id] : null, (signal) => api.growth.finder.candidate(id, signal))
  useDocumentTitle(data?.candidate.name ?? 'Candidate')

  if (!validId || (status === 'error' && isApiError(error) && error.code === 'not_found')) {
    return (
      <Card>
        <EmptyState title="Candidate not found" description="It may be on the do-not-contact list now." action={<ButtonLink to="/growth/finder/queue">Back to the queue</ButtonLink>} />
      </Card>
    )
  }
  if (status === 'loading' && !data) return <LoadingState label="Loading candidate…" />
  if (!data) {
    return (
      <Card>
        <ErrorState error={error} title="Couldn't load this candidate" onRetry={refetch} />
      </Card>
    )
  }

  const { candidate: c, draft } = data
  const open = c.status === 'new'
  // A Facebook or Instagram link can't be read, so there's nothing to run.
  const canCheck = !c.stored_only
  const canAdd = c.verify_status === 'verified' || c.verify_status === 'needs_review'
  const verify = async () => {
    setProblem(null)
    try {
      const run = await api.growth.finder.verify([c.id])
      verifyRun.started(run.id)
    } catch (err) {
      setProblem(errorMessage(err))
    }
  }

  return (
    <div className="grid grid-cols-1 gap-5">
      <PageHeader
        crumbs={[{ label: 'Lead finder', to: '/growth/finder/queue' }, { label: c.name }]}
        title={c.name}
        subtitle={candidatePlace(c) ?? undefined}
        badges={<CandidateStatusBadge status={c.status} />}
        actions={
          <>
            {c.lead_id ? <ButtonLink to={`/growth/leads/${c.lead_id}`}>View lead</ButtonLink> : null}
            {open || c.status === 'duplicate' ? <Button onClick={() => setConfirm('reject')}>Reject</Button> : null}
            {open && canCheck ? (
              <Button disabled={verifyRun.busy} onClick={() => void verify()}>
                {c.verified_at ? 'Verify again' : 'Verify'}
              </Button>
            ) : null}
            {open ? (
              <Button variant="primary" disabled={!canAdd} onClick={() => setConfirm('add')}>
                Add to leads
              </Button>
            ) : null}
          </>
        }
      />

      {c.status === 'duplicate' && c.duplicate_of_lead_id ? (
        <InlineAlert title="Already a lead" action={<ButtonLink size="sm" to={`/growth/leads/${c.duplicate_of_lead_id}`}>View lead</ButtonLink>}>
          This organisation matches a lead you have.
        </InlineAlert>
      ) : null}
      {open && c.verify_status === 'unverified' ? <InlineAlert tone="warn" title="Not verified yet">Nothing joins the leads unchecked. Verify it first.</InlineAlert> : null}
      <FailedReasons facts={c} />
      {problem ? <InlineAlert tone="bad">{problem}</InlineAlert> : null}
      <ActivityRunBanner follow={verifyRun} words={VERIFY_RUN_WORDS} />
      {c.stored_only ? <InlineAlert title="Stored as a link">Facebook and Instagram are never fetched: their terms ban scraping. Open the link yourself to check it.</InlineAlert> : null}

      <div className="grid items-start gap-4 lg:grid-cols-[1.2fr_1fr]">
        <div className="grid content-start gap-4">
          <Card>
            <CardHeader title="Details" description="Public organisation facts only." />
            <KeyValueList
              items={[
                { label: 'Sport', value: c.sport ?? '—' },
                { label: 'Type', value: LEAD_TYPE_LABELS[c.type] },
                { label: 'City', value: c.city ?? '—' },
                { label: 'State', value: c.state ?? '—' },
                { label: 'Country', value: c.country ?? '—' },
                { label: 'Website', value: c.website ? <SafeExternalLink href={c.website} /> : '—' },
                { label: 'Uses now', value: <CurrentPlatform facts={c} linked={c.linked_platform} text={c.current_platform} /> },
                { label: 'Bucket', value: <BucketBadge bucket={c.bucket} /> },
                ...(c.site_signals ? [{ label: 'What the site shows', value: <SiteSignalList site={c.site_signals} /> }] : []),
                { label: 'Public email', value: c.email ? <EmailText email={c.email} /> : '—' },
                { label: 'Public phone', value: c.phone ?? '—' },
                { label: 'Found on', value: <SafeExternalLink href={c.source_url} /> },
                { label: 'Source', value: c.run_id ? <Link to={`/growth/finder/runs/${c.run_id}`} className="underline decoration-line-strong underline-offset-2 hover:text-accent">{SOURCE_LABELS[c.source] ?? c.source}, run #{c.run_id}</Link> : SOURCE_LABELS[c.source] ?? c.source },
              ]}
            />
          </Card>
          <PreviewData candidate={c} draft={draft} onChange={refetch} />
        </div>
        <div className="grid content-start gap-4">
          <Card>
            <CardHeader title="How we checked this" description="Where the league lives today, read from its live site, with the proof." />
            <VerifyDetails facts={c} />
          </Card>
          <Card>
            <CardHeader title="Activity" description="Is the league still running? Only dated games, seasons and registrations count." />
            <ActivityDetails facts={c} />
          </Card>
          <Priority candidate={c} />
          <Card>
            <CardHeader title={`Confidence ${c.confidence}`} description="How sure the finder is that this is a real league." />
            <ul className="grid list-disc gap-1 pl-4 text-[13px] text-ink">
              {c.reasons.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </Card>
        </div>
      </div>

      <ConfirmDialog
        open={confirm === 'add'}
        onClose={() => setConfirm(null)}
        title="Add to leads?"
        description={`This makes a lead with priority ${c.priority}. Nothing is sent to anyone.`}
        confirmLabel="Add to leads"
        tone="primary"
        onConfirm={async () => {
          const out = await api.growth.finder.add(c.id)
          invalidateQueries('growth')
          toast.success('Added to leads.')
          navigate(`/growth/leads/${out.lead.id}`)
        }}
      />
      <ConfirmDialog
        open={confirm === 'reject'}
        onClose={() => setConfirm(null)}
        title="Reject this candidate?"
        description="It won't be suggested again."
        confirmLabel="Reject"
        onConfirm={async () => {
          await api.growth.finder.reject(c.id)
          invalidateQueries('growth')
          toast.success('Rejected.')
          navigate('/growth/finder/queue')
        }}
      />
    </div>
  )
}
